import React, { useState, useEffect, useRef } from "react";
import { Switch } from "antd";
import { ToastContainer, toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import FormControlLabel from "@mui/material/FormControlLabel";
import Avatar from "@mui/material/Avatar";
import { resetUser } from "../../store/features/loginUser";
import { resetAgentUser } from "../../store/features/loginUserTenant";
import { getAgentStatus } from "../../store/features/getAgentStatus";
import { coUserUpdateStatus } from "../../store/features/coUserUpdateStatus";
import { logoutAgent } from "../../store/features/logoutAgent";
import IOSSwitch from "../common/IOSswitch";
import { createAppSocket } from '../../api/createAppSocket';
import useClickOutside from "../../hooks/useClickOutside";
import { useSocket } from "../../SocketContext";
import { ReactComponent as GreenDot } from "../../assets/images/green-dot.svg";
import { ReactComponent as RedDot } from "../../assets/images/red-dot.svg";
import { ReactComponent as GreyDot } from "../../assets/images/grey-dot.svg";
import { getUserSessions } from '../../store/features/getUserSessions';
import { logoutSingleDevice } from '../../store/features/LogoutSingleDevice';
import { createPortal } from "react-dom";
import { clearSessionScopedState } from "../../utils/clearSessionScopedState";

function LogoutMenu({ onClose, parentRef, acceptChat, handleUpdateChatStatus, setAcceptChat, isStatusLoading, onLogoutClick }) {
  const userData = useSelector((state) => state.loginToken);
  console.log("userData", userData);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const socketRef = useRef();
  const menuRef = useRef();
  const [logoutLoading, setLogoutLoading] = useState(false);

  const handleClickOutside = (event) => {
    if (
      menuRef.current &&
      !menuRef.current.contains(event.target) &&
      parentRef &&
      !parentRef.current.contains(event.target)
    ) {
      onClose();
    }
  };

  useClickOutside(menuRef, handleClickOutside);

  useEffect(() => {
    socketRef.current = createAppSocket();

    socketRef.current.on("get-status", handleUpdateChatStatus);

    return () => {
      socketRef.current?.off("get-status", handleUpdateChatStatus);
      // socketRef.current.disconnect();
    };
  }, []);


  const handleChatChange = async (event) => {
    const isChecked = event.target.checked;
    const agentId = userData?.response?.user?._id;
    setAcceptChat(isChecked);

    try {
      const result = await dispatch(
        coUserUpdateStatus({
          id: agentId,
          requestHeaders: { accept_chat: isChecked },
        })
      );

      if (!result?.payload) {
        toast.error("Some error occurred. Please try again later.");
        return;
      }

      if (result.payload.status === 200) {
        socketRef.current.emit("update-status", agentId, isChecked);

        toast.success("User status updated successfully", {
          toastId: `chat-status-${agentId}`,
        })
      } else {
        toast.error(result.payload.message || "Failed to update chat status");
      }
    } catch (err) {
      toast.error("Unexpected error occurred");
    }
  };

  const setAllRoomsOfflineOnLogout = () => {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      // Check if the key starts with 'room_'
      if (key.startsWith("room_")) {
        const roomData = JSON.parse(localStorage.getItem(key)) || {};

        // Set isOnline to false
        roomData.isOnline = false;

        // Save the updated room data back to localStorage
        localStorage.setItem(key, JSON.stringify(roomData));
      }
    }
  };

  const logoutThisDevice = async () => {
    const result = await dispatch(logoutSingleDevice());
    if (logoutSingleDevice.rejected.match(result)) {
      toast.error("Couldn't sign you out. Check your connection and try again.");
      return;
    }
    socketRef.current.emit("logout-single-agent", userData?.response?.user?._id);
    dispatch(resetUser());
    dispatch(resetAgentUser());
    clearSessionScopedState();
    localStorage.setItem("logout", Date.now());
    navigate("/login", { replace: true });
  };

  const handleLogoutButton = async () => {
    setLogoutLoading(true);
    try {
      const result = await dispatch(getUserSessions());
      const sessionList = result.payload?.sessions || [];
      if (Array.isArray(sessionList) && sessionList.length > 1) {
        onLogoutClick();
        console.log("Multiple sessions detected, showing logout popup.", sessionList.length);
      } else {
        await logoutThisDevice();
      }
    } catch (e) {
      await logoutThisDevice();
    } finally {
      setLogoutLoading(false);
    }
  };

  const chracterPosition = (alphabet) => {
    const alphabets = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const positionOfAlphabet = alphabets.indexOf(alphabet) + 1;
    return positionOfAlphabet;
  };
  const colorCodesForAvatar = [
    "#405261", // A
    "#FF4D4F", // B
    "#FA541C", // C
    "#FA8C16", // D
    "#FAAD14", // E
    "#EB2F96", // F
    "#FADB14", // G
    "#A0D911", // H
    "#52C41A", // I
    "#5CDBD3", // J
    "#9254DE", // K
    "#F759AB", // L
    "#2F54EB", // M
    "#722ED1", // N
    "#1890FF", // O
    "#13C2C2", // P
    "#2DB7F5", // Q
    "#19BE6B", // R
    "#30A745", // S
    "#FA8C16", // T
    "#FAAD14", // U
    "#9254DE", // V
    "#F759AB", // W
    "#13C2C2", // X
    "#722ED1", // Y
    "#2DB7F5", // Z
  ];


  return createPortal(
    <div
      className="Logout-Menu-Container"
      ref={menuRef}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="sidebar-footer-logout">
        <div className="footer-container-logout">
          <div className="sidebar-profile-avatar-wrap">
            {userData?.response?.user &&
              (userData?.response?.user?.agent_image ? (
                <Avatar
                  src={userData.response.user.agent_image}
                  alt={userData.response.user.full_name}
                  sx={{ width: 32, height: 32, fontSize: 14 }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: 14,
                    bgcolor:
                      colorCodesForAvatar[
                      chracterPosition(
                        userData?.response?.user?.full_name
                          .substring(0, 1)
                          .toUpperCase()
                      )
                      ],
                  }}
                >
                  {userData?.response?.user?.full_name
                    .substring(0, 1)
                    .toUpperCase()}
                </Avatar>
              ))}
            <span className="sidebar-profile-status">
              {isStatusLoading ? <GreyDot /> : (acceptChat ? <GreenDot /> : <RedDot />)}
            </span>
          </div>
          <div className="weilJenny-container-logout">
            <p className="wilson">{userData?.response?.user?.full_name}</p>
            <p className="jenny">{userData?.response?.user?.email}</p>
          </div>
        </div>
      </div>
      <div className="Logout-switch-container">
        <p className="switch-class">Accept chats</p>
        <FormControlLabel
          sx={{ marginRight: "0px" }}
          control={
            <IOSSwitch checked={acceptChat} onChange={handleChatChange} />
          }
        />
      </div>
      {/* <div className="Logout-switch-Dark">
        <p className="switch-class">Dark mode</p>
        <FormControlLabel sx={{ marginRight: "0px" }} control={<IOSSwitch />} />
      </div> */}
      <p className="LineClass" />
      <p className="switch-class">Download apps</p>
      <p className="LineClass" />
      <div className="Switch-Main-Container">
        <p className="switch-class">Help center</p>
        <p className="switch-class">Report an issue</p>
        <p className="switch-class" onClick={() => navigate("/settings/personal/notification-preferences")}>Notification preferences</p>
        <p className="switch-class">Keyboard shortcuts</p>
      </div>
      <p className="LineClass" />
      <p
        className="LogoutText"
        onClick={handleLogoutButton}
        style={{ opacity: logoutLoading ? 0.6 : 1, pointerEvents: logoutLoading ? 'none' : 'auto' }}
      >
        {logoutLoading ? 'Loading...' : 'Logout'}
      </p>
    </div>,
    document.body
  );
}
export default LogoutMenu;